import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { apiGet, apiPost, apiDelete } from '../api/client.js';
import { platformName, platformIcon } from '../lib/platforms.js';
import { TikTokDefaultsButton } from './TikTokOptions.jsx';

/**
 * Connections section on ProjectDetail (ADR-0007).
 *
 * - Lists every social_account for the project (live).
 * - For each supported-but-not-yet-connected platform, shows a "Connect" tile
 *   that kicks off OAuth via POST /social/connect/init then redirects the
 *   browser to the platform's authorization URL.
 * - Renders the per-project "Auto-publish on approve" toggle (admin+).
 * - Shows a success toast when ?connected=<platform> is in the URL after
 *   the OAuth round-trip completes.
 *
 * Platforms supported today: instagram. LinkedIn/X/TikTok land via the
 * same flow when their adapter ships (one new entry in the workers'
 * platform registry, no UI change here required other than the catalog
 * below).
 */
// Connection-specific copy only; name + icon come from the shared
// PLATFORM_META (one source of truth). `displayName`/`icon` are resolved at
// render time via platformName()/platformIcon().
const PLATFORM_CATALOG = [
  {
    platform: 'instagram',
    helperText: 'Auto-publish carousels, reels, and stories. Requires an IG Business or Creator account linked to a Facebook Page.',
  },
  {
    platform: 'linkedin',
    helperText: 'Auto-publish text and image posts to a LinkedIn Company Page. Requires the app’s Community Management API access (approved by LinkedIn) and admin access to the Page.',
  },
  {
    platform: 'x',
    helperText: 'Auto-publish tweets with up to 4 images to your X (Twitter) account. Video is not supported yet.',
  },
  {
    platform: 'facebook',
    helperText: 'Auto-publish image posts and updates to a Facebook Page you admin. A separate connection from Instagram.',
  },
  {
    platform: 'tiktok',
    helperText: 'Auto-publish photo posts to your TikTok account. Until the app passes TikTok’s content audit, posts are private (visible only to the account) — video posting comes later.',
  },
];

function fmtAgo(iso) {
  if (!iso) return null;
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return iso; }
}

export function ConnectionsSection({ projectKey, project }) {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [toast, setToast] = useState(null);

  // Pick up ?connected=<platform> / ?social_error=<code> from OAuth round-trip
  useEffect(() => {
    const connected = searchParams.get('connected');
    const social_error = searchParams.get('social_error');
    if (connected) {
      const warn = searchParams.get('connect_warning');
      const warnProject = searchParams.get('warn_project');
      const platformName = connected.charAt(0).toUpperCase() + connected.slice(1);
      if (warn === 'account_in_use') {
        // Connect-time guard: the same account is already active on another
        // project in this org (e.g. connecting Markaii's TikTok while on the
        // CliLancers project). Non-blocking — just flag it so the wrong
        // account doesn't silently receive this project's posts.
        setToast({
          kind: 'warn',
          text: `${platformName} connected — but this account is already active on ${warnProject ? `the “${warnProject}” project` : 'another project'}. If that’s not intended, disconnect it below.`,
        });
      } else {
        setToast({ kind: 'ok', text: `${platformName} connected.` });
      }
      // Clean the URL so a refresh doesn't re-toast
      searchParams.delete('connected');
      searchParams.delete('connect_warning');
      searchParams.delete('warn_project');
      setSearchParams(searchParams, { replace: true });
      queryClient.invalidateQueries({ queryKey: ['social', 'accounts', projectKey] });
      queryClient.invalidateQueries({ queryKey: ['loop-status', projectKey] });
      const t = setTimeout(() => setToast(null), warn ? 12000 : 6000);
      return () => clearTimeout(t);
    }
    if (social_error) {
      setToast({ kind: 'err', text: `Connection failed: ${social_error.replace(/_/g, ' ')}` });
      searchParams.delete('social_error');
      setSearchParams(searchParams, { replace: true });
      const t = setTimeout(() => setToast(null), 8000);
      return () => clearTimeout(t);
    }
  }, [searchParams, setSearchParams, queryClient, projectKey]);

  const accountsQ = useQuery({
    queryKey: ['social', 'accounts', projectKey],
    queryFn: ({ signal }) => apiGet(`/social/accounts?project=${encodeURIComponent(projectKey)}`, { signal }),
  });

  const connectM = useMutation({
    mutationFn: ({ platform, auth_method }) =>
      apiPost('/social/connect/init', { project_key: projectKey, platform, auth_method }),
    onSuccess: (res) => {
      // Redirect the whole window to the platform's OAuth page
      window.location.assign(res.authorization_url);
    },
    onError: (err) => setToast({ kind: 'err', text: err.body?.error || err.message }),
  });

  const disconnectM = useMutation({
    mutationFn: (id) => apiDelete(`/social/accounts/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social', 'accounts', projectKey] });
      queryClient.invalidateQueries({ queryKey: ['loop-status', projectKey] });
      setToast({ kind: 'ok', text: 'Account disconnected.' });
      setTimeout(() => setToast(null), 4000);
    },
    onError: (err) => setToast({ kind: 'err', text: err.body?.error || err.message }),
  });

  const archiveM = useMutation({
    mutationFn: (id) => apiPost(`/social/accounts/${id}/archive`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social', 'accounts', projectKey] });
      setToast({ kind: 'ok', text: 'Removed from the list — reconnecting the same account restores it.' });
      setTimeout(() => setToast(null), 4000);
    },
    onError: (err) => setToast({ kind: 'err', text: err.body?.error || err.message }),
  });

  const autoReplyM = useMutation({
    mutationFn: (enabled) => apiPost(`/social/projects/${projectKey}/auto-reply`, { enabled }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['project', projectKey] });
      setToast({
        kind: 'ok',
        text: res.auto_reply_enabled
          ? 'Auto-reply ON — comments get answered as this brand automatically (safety-checked; edge cases wait in the Inbox).'
          : 'Auto-reply OFF — comment replies wait for your approval in the Inbox.',
      });
      setTimeout(() => setToast(null), 6000);
    },
    onError: (err) => setToast({ kind: 'err', text: err.body?.error || err.message }),
  });

  const autoPublishM = useMutation({
    mutationFn: (enabled) => apiPost(`/social/projects/${projectKey}/auto-publish`, { enabled }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['project', projectKey] });
      setToast({
        kind: 'ok',
        text: res.auto_publish_enabled
          ? 'Auto-publish ON — approved posts will ship to connected accounts immediately.'
          : 'Auto-publish OFF — approval no longer triggers publish.',
      });
      setTimeout(() => setToast(null), 6000);
    },
    onError: (err) => setToast({ kind: 'err', text: err.body?.error || err.message }),
  });

  const accounts = accountsQ.data?.accounts || [];
  // Deliberately-disconnected rows stay in the DB (they anchor publication
  // history) but have no UI job: they can't publish and the catalog tile
  // below already offers reconnect. Expired/refresh_failed DO render — those
  // are actionable "reconnect me" states, not user choices.
  const visibleAccounts = accounts.filter((a) => a.status !== 'disconnected');
  const connectedPlatforms = new Set(accounts.filter((a) => a.status === 'active').map((a) => a.platform));

  return (
    <section className="post-section">
      <div className="section-h-row">
        <h2>Connections</h2>
        <span className="muted small">Per-project social accounts + auto-publish toggle</span>
      </div>

      {toast && <div className={`post-action-msg ${toast.kind}`}>{toast.text}</div>}

      <div className="connection-list">
        {accountsQ.isLoading && <div className="loading">Loading connections…</div>}

        {/* Already-connected accounts */}
        {visibleAccounts.map((acct) => (
          <div key={acct.id} className={`connection-card connection-${acct.status}`}>
            <div className="connection-head">
              <span className="connection-icon">{platformIcon(acct.platform)}</span>
              <div className="connection-meta">
                <div className="connection-name">{acct.display_name}</div>
                <div className="connection-handle">{acct.external_account_handle}</div>
              </div>
              <span className={`connection-status connection-status-${acct.status}`}>{acct.status}</span>
              {acct.status !== 'active' && (
                <button
                  type="button"
                  className="btn-icon"
                  title="Remove this dead connection from the list (history is kept; reconnecting restores it)"
                  aria-label={`Remove ${acct.display_name} ${acct.external_account_handle} from the list`}
                  disabled={archiveM.isPending}
                  onClick={() => archiveM.mutate(acct.id)}
                >
                  ✕
                </button>
              )}
            </div>
            <div className="connection-detail muted small">
              {acct.display_label && <span>{acct.display_label} · </span>}
              {/* last_refreshed_at moves on every reconnect; connected_at
                  never does — showing the former is what makes a fresh
                  reconnect visibly "take" (owner-reported confusion
                  2026-07-26: card said "1 month ago" after reconnecting). */}
              Connected {fmtAgo(acct.last_refreshed_at || acct.connected_at)}
              {/* Renewal date only makes sense for a LIVE token. An expired
                  account was usually revoked by the platform ahead of
                  expires_at (Meta dev-mode revocations), so showing
                  "renews in 2 months" next to an EXPIRED badge was a lie —
                  say what actually happened and point at the fix. */}
              {acct.status === 'active' && acct.expires_at && <span> · token renews {fmtAgo(acct.expires_at)}</span>}
              {acct.status === 'expired' && <span> · access revoked by the platform — reconnect below</span>}
            </div>
            {acct.status === 'active' && (
              <div className="connection-actions">
                <button
                  type="button"
                  className="btn-danger btn-compact"
                  disabled={disconnectM.isPending}
                  onClick={() => {
                    if (window.confirm(`Disconnect ${acct.display_name} (${acct.external_account_handle})?`)) {
                      disconnectM.mutate(acct.id);
                    }
                  }}
                >
                  {disconnectM.isPending ? 'Disconnecting…' : 'Disconnect'}
                </button>
                {acct.platform === 'tiktok' && (
                  <TikTokDefaultsButton account={acct} projectKey={projectKey} />
                )}
                {acct.platform === 'linkedin' && (
                  <LinkedInOrgPicker account={acct} projectKey={projectKey} setToast={setToast} />
                )}
              </div>
            )}
          </div>
        ))}

        {/* Connectable platforms not yet linked */}
        {PLATFORM_CATALOG.filter((p) => !connectedPlatforms.has(p.platform)).map((p) => (
          <div key={p.platform} className="connection-card connection-empty">
            <div className="connection-head">
              <span className="connection-icon">{platformIcon(p.platform)}</span>
              <div className="connection-meta">
                <div className="connection-name">{platformName(p.platform)}</div>
                <div className="connection-handle muted">Not connected</div>
              </div>
            </div>
            <p className="connection-detail">{p.helperText}</p>

            {p.platform === 'instagram' ? (
              <>
                {/* Direct Instagram login (no Facebook Page) — the recommended path. */}
                <button
                  type="button"
                  className="btn btn-compact"
                  disabled={connectM.isPending}
                  onClick={() => connectM.mutate({ platform: 'instagram', auth_method: 'instagram_login' })}
                >
                  {connectM.isPending ? 'Starting…' : 'Connect Instagram (direct login)'}
                </button>
                {/* Legacy path, for users who specifically want to publish through a
                    Facebook Page — the preflight checklist applies only here. */}
                <details className="connection-alt">
                  <summary className="muted small">Or connect via a Facebook Page</summary>
                  <InstagramPreflight />
                  <MultiPageGrantNote />
                  <button
                    type="button"
                    className="btn btn-compact btn-outline"
                    disabled={connectM.isPending}
                    onClick={() => connectM.mutate({ platform: 'instagram', auth_method: 'facebook_page' })}
                  >
                    Connect via Facebook Page
                  </button>
                </details>
              </>
            ) : (
              <>
                {p.platform === 'facebook' && <MultiPageGrantNote />}
                <button
                  type="button"
                  className="btn btn-compact"
                  disabled={connectM.isPending}
                  onClick={() => connectM.mutate({ platform: p.platform })}
                >
                  {connectM.isPending ? 'Starting OAuth…' : `Connect ${platformName(p.platform)}`}
                </button>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Auto-publish toggle */}
      <div className="auto-publish-toggle">
        <div className="auto-publish-label">
          <strong>Auto-publish on approval</strong>
          <p className="muted small">
            {project.auto_publish_enabled
              ? 'ON — approving a post immediately publishes to every connected account for this project.'
              : 'OFF — approving a post moves it to “approved”; you click “Publish now” per-post when ready.'}
          </p>
        </div>
        <button
          type="button"
          className={`pillar-toggle ${project.auto_publish_enabled ? 'on' : 'off'}`}
          disabled={autoPublishM.isPending || connectedPlatforms.size === 0}
          title={connectedPlatforms.size === 0
            ? 'Connect at least one account before enabling auto-publish'
            : `Click to ${project.auto_publish_enabled ? 'disable' : 'enable'} auto-publish`}
          onClick={() => autoPublishM.mutate(!project.auto_publish_enabled)}
        >
          <span className="pillar-toggle-knob" />
          <span className="pillar-toggle-label">{project.auto_publish_enabled ? 'on' : 'off'}</span>
        </button>
      </div>

      {/* Auto-reply toggle (migration 041) */}
      <div className="auto-publish-toggle">
        <div className="auto-publish-label">
          <strong>Auto-reply to comments</strong>
          <p className="muted small">
            {project.auto_reply_enabled
              ? 'ON — comments on this brand’s posts get answered automatically, in the brand’s voice, as its support rep. Replies the safety check can’t clear wait in the Inbox instead.'
              : 'OFF — AI drafts a reply for every comment, but each one waits for your approval in the Inbox before posting.'}
          </p>
        </div>
        <button
          type="button"
          className={`pillar-toggle ${project.auto_reply_enabled ? 'on' : 'off'}`}
          disabled={autoReplyM.isPending || connectedPlatforms.size === 0}
          title={connectedPlatforms.size === 0
            ? 'Connect at least one account before enabling auto-reply'
            : `Click to ${project.auto_reply_enabled ? 'disable' : 'enable'} auto-reply`}
          onClick={() => autoReplyM.mutate(!project.auto_reply_enabled)}
        >
          <span className="pillar-toggle-knob" />
          <span className="pillar-toggle-label">{project.auto_reply_enabled ? 'on' : 'off'}</span>
        </button>
      </div>
    </section>
  );
}

/**
 * Shared-grant warning shown on the Facebook + via-Facebook-Page Instagram
 * connect flows.
 *
 * All brands share ONE Meta app, and Meta stores the "which Pages can this app
 * access" grant once per (Facebook user + app) — not per connection. If the
 * user opts in to only ONE Page at Meta's "Choose the Pages" screen, Meta
 * REVOKES the app's access to every other Page, silently killing the stored
 * Page token of any sibling project (a Markaii reconnect would break
 * CliLancers' Facebook, and vice-versa — observed 2026-06-23). The fix is to
 * always grant ALL Pages; markaii's brand-aware page hint then routes each
 * project to its own Page. This note makes that non-obvious requirement
 * impossible to miss.
 */
function MultiPageGrantNote() {
  return (
    <p className="connection-note">
      <strong>On Meta's “Choose the Pages” screen, select ALL your Pages</strong>{' '}
      (or “Opt in to all current and future Pages”). Your brands share one Meta
      app, so picking only one Page revokes the others and breaks their
      publishing. Markaii automatically routes this project to its own Page.
    </p>
  );
}

/**
 * Pre-flight checklist surfaced above the "Connect Instagram" button.
 *
 * Markaii can only verify these via the OAuth round-trip (Meta's Graph
 * API gates them behind the access token), but the failure modes are
 * predictable and frequent:
 *
 *   1. The IG account is "Personal" (not Business/Creator) → OAuth ends
 *      on "You don't have any Instagram accounts" screen.
 *   2. The IG account is Business but isn't linked to a FB Page → same.
 *   3. The signed-in FB user isn't admin of the Page → empty Pages list.
 *
 * Surfacing each prerequisite with a deep link + "I've done this" toggle
 * lets the user fix the missing piece BEFORE they burn an OAuth attempt
 * and end up confused. The checks are advisory, not blocking — the
 * Connect button stays clickable so an experienced operator can skip.
 */
function InstagramPreflight() {
  const [checked, setChecked] = useState({
    professional: false,
    pageExists: false,
    pageLinked: false,
  });

  const allChecked = checked.professional && checked.pageExists && checked.pageLinked;

  return (
    <details className="ig-preflight" open={!allChecked}>
      <summary>
        {allChecked
          ? '✓ Pre-flight done — Instagram is ready to connect'
          : 'Before you connect — 3-step pre-flight'}
      </summary>
      <ol className="ig-preflight-list">
        <li>
          <label>
            <input
              type="checkbox"
              checked={checked.professional}
              onChange={(e) => setChecked((c) => ({ ...c, professional: e.target.checked }))}
            />
            <span>
              <strong>Your IG account is set to Professional (Business or Creator).</strong>
              <br />
              <span className="muted small">
                On your phone: Instagram → your profile → ☰ menu → Settings and privacy → Account type and tools.
                If it says "Switch to Professional", tap it → pick Business → walk through the setup.
              </span>
            </span>
          </label>
        </li>
        <li>
          <label>
            <input
              type="checkbox"
              checked={checked.pageExists}
              onChange={(e) => setChecked((c) => ({ ...c, pageExists: e.target.checked }))}
            />
            <span>
              <strong>You have a Facebook Page for this brand.</strong>
              <br />
              <span className="muted small">
                Visit{' '}
                <a href="https://www.facebook.com/pages/?category=your_pages" target="_blank" rel="noreferrer">
                  facebook.com/pages
                </a>
                {' '}— you should see the Page listed. If not, create one at{' '}
                <a href="https://www.facebook.com/pages/create" target="_blank" rel="noreferrer">
                  facebook.com/pages/create
                </a>.
              </span>
            </span>
          </label>
        </li>
        <li>
          <label>
            <input
              type="checkbox"
              checked={checked.pageLinked}
              onChange={(e) => setChecked((c) => ({ ...c, pageLinked: e.target.checked }))}
            />
            <span>
              <strong>Your IG account is linked to that Facebook Page.</strong>
              <br />
              <span className="muted small">
                Easiest path: visit your Facebook Page → Settings → Linked accounts → Instagram → Connect →
                enter your Instagram credentials. After connecting, when you click "Connect Instagram" below,
                Meta will offer to add both to your Business Portfolio in one step.
              </span>
            </span>
          </label>
        </li>
      </ol>
      <p className="ig-preflight-note muted small">
        If any of these aren't true when you click Connect Instagram, Meta will show "You don't have any
        Instagram accounts" — you'll have to fix the missing piece and start the connect over. We can't
        check this from outside the OAuth flow, hence the manual checklist.
      </p>
    </details>
  );
}

/**
 * "Change page" selector for LinkedIn connections. One member usually admins
 * every brand page in the portfolio; the connect flow auto-picks by
 * brand-name match (metaPages-style hint), and this is the manual override
 * when that guess is wrong — or when a project should deliberately post as
 * a different Company Page.
 */
function LinkedInOrgPicker({ account, projectKey, setToast }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const orgsQ = useQuery({
    queryKey: ['linkedin-orgs', account.id],
    queryFn: ({ signal }) => apiGet(`/social/accounts/${account.id}/orgs`, { signal }),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const selectM = useMutation({
    mutationFn: (org_urn) => apiPost(`/social/accounts/${account.id}/select-org`, { org_urn }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['social', 'accounts', projectKey] });
      setOpen(false);
      setToast({
        kind: 'ok',
        text: `LinkedIn will now post as ${res.social_account?.external_account_handle || 'the selected page'}.`,
      });
    },
    onError: (err) => setToast({ kind: 'err', text: err.body?.detail || err.body?.error || err.message }),
  });

  return (
    <div className="linkedin-org-picker">
      {/* Gear trigger — shares the connection-gear styling with the TikTok
          options gear so per-connection controls look consistent. */}
      <button
        type="button"
        className={`btn btn-ghost btn-icon tt-opt-btn ${open ? 'tt-opt-set' : ''}`}
        title={open ? 'Close page picker' : 'Change LinkedIn page'}
        aria-label={open ? 'Close LinkedIn page picker' : 'Change LinkedIn page'}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        ⚙
      </button>
      {open && (
        <div className="linkedin-org-list">
          {orgsQ.isLoading && <span className="muted small">Loading your Company Pages…</span>}
          {orgsQ.isError && (
            <span className="muted small">
              Couldn’t load pages: {orgsQ.error.body?.detail || orgsQ.error.message}
            </span>
          )}
          {orgsQ.data?.orgs?.map((o) => {
            const current = o.orgUrn === orgsQ.data.current;
            return (
              <button
                key={o.orgUrn}
                type="button"
                className={`linkedin-org-option${current ? ' is-current' : ''}`}
                disabled={current || selectM.isPending}
                onClick={() => selectM.mutate(o.orgUrn)}
              >
                {o.name}{current ? ' — current' : ''}
              </button>
            );
          })}
          {orgsQ.data && !orgsQ.data.orgs?.length && (
            <span className="muted small">No admin Company Pages found for this LinkedIn account.</span>
          )}
        </div>
      )}
    </div>
  );
}
